import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import {
  isPortainerConnectionValid,
  type RegistryConnection,
  type RegistryRepository,
  type RegistryTag,
} from "../infrastructure.types";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/** Sortable columns of the per-repository tag tables. */
export type RegistryTagSortKey = "tag" | "digest" | "lastUpdated";

/** Sort keys that read best newest-first on first click. */
const TAG_DESC_FIRST: ReadonlySet<RegistryTagSortKey> = new Set(["lastUpdated"]);

interface FindResponse<T> {
  data?: T[];
  total?: number;
}
const rowsOf = <T,>(r: FindResponse<T> | T[] | undefined): T[] =>
  Array.isArray(r) ? r : (r?.data ?? []);

/**
 * View store for the Registry Images (Azure Container Registry catalog) view.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore.
 *
 * Entity-cache rule exception: the `registry-repositories` and `registry-tags`
 * services are READ-THROUGH, parameterized by (connection, repository) — not a
 * CRUD entity set, and tag names ("latest") collide across repositories, so the
 * ServiceStore entity cache (idField "name") cannot hold them without clobber.
 * We therefore hold results in local observable maps populated from `.find()`,
 * exactly as the Deploy picker (InstanceActionsStore.deployTags) already does.
 *
 * The registry CONNECTION list, by contrast, is a real CRUD entity set and is
 * read as a computed over the registered `registry-connections` ServiceStore.
 *
 * RBAC: reads are staff (viewer+); the API enforces the read gate. This view is
 * read-only — no mutating actions.
 */
class RegistryImagesStore {
  rootStore: RootStore;

  loadingRepos = false;
  /** The selected registry's repositories failed to load. */
  error: string | null = null;
  /** The registry-connection list failed to load — without this the page read
   *  "No verified registry connections. Add and validate one…". */
  connectionsError: string | null = null;

  /** Which registry connection's catalog is shown. */
  selectedConnectionId: string | null = null;

  /** Repository-name filter (header search). */
  searchQuery = "";

  /** Repositories for the selected connection (read-through, held locally). */
  repositories: RegistryRepository[] = [];

  /** Which repository rows are expanded to show their tags. */
  private expanded = new Set<string>();

  /** Tags per repository, keyed by repository name (read-through, local). */
  tagsByRepo = new Map<string, RegistryTag[]>();
  /** Repositories whose tag load is in flight. */
  loadingTags = new Set<string>();
  /** Repositories whose tag load succeeded with no tags. */
  tagsUnavailable = new Set<string>();
  /** Repositories whose tag load FAILED, with the reason — never "No tags". */
  tagsErrors = new Map<string, string>();

  // Column sort for the per-repo tag tables, keyed PER REPOSITORY.
  //
  // It used to be one shared pair, so sorting one expanded repo re-sorted every
  // other one — and with two repos open on a phone the table you did not touch
  // is usually the one off-screen. Default: last-updated, newest first.
  tagSorts = new Map<string, { key: RegistryTagSortKey; dir: SortDir }>();

  /** The active sort for one repository, defaulted. */
  tagSortFor(repository: string): { key: RegistryTagSortKey; dir: SortDir } {
    return (
      this.tagSorts.get(repository) ?? {
        key: "lastUpdated" as RegistryTagSortKey,
        dir: "desc" as SortDir,
      }
    );
  }

  /**
   * Bumped by every connection change and by reset(): a repository or tag read
   * started before one answers about a catalog that is no longer on screen.
   */
  private catalogSeq = 0;
  /** The latest tag load per repository (a forced refresh supersedes an older call). */
  private tagCalls = new Map<string, number>();
  private tagCallSeq = 0;
  /**
   * Bumped by every `loadRepositories` call: two reads for the SAME connection
   * share a `catalogSeq`, so Refresh (or a re-entry) while one is in flight
   * would otherwise let the first to settle clear the other's spinner, and the
   * older answer overwrite the newer one.
   */
  private repoCallSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable<this, "tagCalls" | "tagCallSeq" | "catalogSeq" | "repoCallSeq">(this, {
      rootStore: false,
      tagCalls: false,
      tagCallSeq: false,
      catalogSeq: false,
      repoCallSeq: false,
    });
  }

  // ---- Service seams ----

  private get connectionsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("registry-connections") ??
        this.rootStore.registryConnectionsStore) as unknown as
        | ServiceStoreType
        | null) ?? null
    );
  }

  private get repositoriesService(): ServiceStoreType | null {
    return (
      (this.rootStore.serviceStoreManager?.getStore(
        "registry-repositories"
      ) as unknown as ServiceStoreType | null) ?? null
    );
  }

  private get tagsService(): ServiceStoreType | null {
    return (
      (this.rootStore.serviceStoreManager?.getStore(
        "registry-tags"
      ) as unknown as ServiceStoreType | null) ?? null
    );
  }

  // ---- Connections (computed over the CRUD ServiceStore cache) ----

  get connections(): RegistryConnection[] {
    return (this.connectionsService?.entities ??
      []) as unknown as RegistryConnection[];
  }

  /** Only registries we can actually read from — verified connections. */
  get selectableConnections(): RegistryConnection[] {
    return this.connections.filter((c) => isPortainerConnectionValid(c.status));
  }

  get selectedConnection(): RegistryConnection | null {
    return (
      this.connections.find((c) => c.id === this.selectedConnectionId) ?? null
    );
  }

  // ---- Derived views ----

  get filteredRepositories(): RegistryRepository[] {
    const q = this.searchQuery.trim().toLowerCase();
    const list = q
      ? this.repositories.filter((r) => r.name.toLowerCase().includes(q))
      : this.repositories;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }

  get repositoryCount(): number {
    return this.repositories.length;
  }

  /** Distinct tags loaded so far across expanded repositories. */
  get loadedTagCount(): number {
    let n = 0;
    for (const tags of this.tagsByRepo.values()) n += tags.length;
    return n;
  }

  /** True when any read-through result was served from stale cache. */
  get isStale(): boolean {
    if (this.repositories.some((r) => r.stale)) return true;
    for (const tags of this.tagsByRepo.values()) {
      if (tags.some((t) => t.stale)) return true;
    }
    return false;
  }

  isExpanded(repository: string): boolean {
    return this.expanded.has(repository);
  }

  tagsFor(repository: string): RegistryTag[] {
    return this.tagsByRepo.get(repository) ?? [];
  }

  /**
   * One repository's tags under the active column sort (what the expanded row
   * renders). Never mutates the stored list; tags with no update/create time
   * sort last in either direction.
   */
  sortedTagsFor(repository: string): RegistryTag[] {
    const { key, dir } = this.tagSortFor(repository);
    const valueOf = (t: RegistryTag): SortValue => {
      switch (key) {
        case "tag":
          return textValue(t.name);
        case "digest":
          return textValue(t.digest);
        case "lastUpdated":
          return dateValue(t.lastUpdateTime ?? t.createdTime);
      }
    };
    return sortRows(this.tagsFor(repository), valueOf, dir);
  }

  // ---- Actions ----

  setSearchQuery(value: string) {
    this.searchQuery = value;
  }

  /** Tag-column header click: same column toggles direction, a new column
   *  starts in its natural direction (last-updated newest-first, text A→Z).
   *  Scoped to the repository whose header was clicked. */
  setTagSort(repository: string, key: RegistryTagSortKey) {
    const current = this.tagSortFor(repository);
    const next = nextSort(current.key, current.dir, key, TAG_DESC_FIRST);
    // Replace the Map so MobX sees the change (makeAutoObservable wraps it,
    // but a fresh instance is unambiguous and this list is tiny).
    const updated = new Map(this.tagSorts);
    updated.set(repository, { key: next.key, dir: next.dir });
    this.tagSorts = updated;
  }

  /** Pick a registry connection and load its repositories. */
  selectConnection(connectionId: string | null) {
    this.selectedConnectionId = connectionId;
    // A read still in flight for the previous connection must neither write
    // its rows here nor clear a flag the next read set.
    this.catalogSeq++;
    this.loadingRepos = false;
    this.repositories = [];
    this.expanded.clear();
    this.tagsByRepo.clear();
    this.loadingTags.clear();
    this.tagsUnavailable.clear();
    this.tagsErrors.clear();
    if (connectionId) void this.loadRepositories();
  }

  /**
   * Entry point from the route. Seeds the connection list, then auto-selects a
   * single verified registry (the common case) so the catalog shows at once.
   */
  async initialize() {
    const ended = sessionMark(this.rootStore);
    await this.loadConnections();
    // Signed out during the read: nothing to auto-select or load for anyone.
    if (ended()) return;
    if (!this.selectedConnectionId) {
      const options = this.selectableConnections;
      if (options.length === 1) this.selectConnection(options[0].id);
      else if (options.length > 1) this.selectConnection(options[0].id);
    } else {
      void this.loadRepositories();
    }
  }

  async loadConnections() {
    this.connectionsError = null;
    const ended = sessionMark(this.rootStore);
    try {
      await findOrThrow(this.connectionsService, { query: {} });
    } catch (err) {
      runInAction(() => {
        // A failure that lands after the sign-out (a 401 on the old token) is
        // not the next person's.
        if (!isSessionEnded(err) && !ended()) {
          this.connectionsError = loadErrorMessage(
            err,
            "Failed to load registry connections"
          );
        }
      });
      console.error("Failed to load registry connections:", err);
    }
  }

  async loadRepositories() {
    const connectionId = this.selectedConnectionId;
    if (!connectionId) return;
    const seq = this.catalogSeq;
    const call = ++this.repoCallSeq;
    const ended = sessionMark(this.rootStore);
    const current = () => seq === this.catalogSeq && call === this.repoCallSeq && !ended();
    this.loadingRepos = true;
    this.error = null;
    try {
      const result = await findOrThrow(this.repositoriesService, {
        query: { registryConnectionId: connectionId },
      });
      const rows = rowsOf<RegistryRepository>(
        result as unknown as FindResponse<RegistryRepository>
      );
      runInAction(() => {
        if (!current()) return;
        this.repositories = rows;
        // Re-load tags for any rows still expanded across a refresh.
        for (const repo of this.expanded) void this.loadTags(repo, true);
      });
    } catch (err: unknown) {
      // Signed out under this call: what the reset left is the next person's.
      if (isSessionEnded(err)) return;
      console.error("Failed to load registry repositories:", err);
      runInAction(() => {
        if (!current()) return;
        this.repositories = [];
        this.error = loadErrorMessage(err, "Failed to load repositories");
      });
    } finally {
      runInAction(() => {
        if (current()) this.loadingRepos = false;
      });
    }
  }

  /** Toggle a repository row; lazy-loads its tags the first time it opens. */
  toggleRepository(repository: string) {
    if (this.expanded.has(repository)) {
      this.expanded.delete(repository);
      return;
    }
    this.expanded.add(repository);
    if (!this.tagsByRepo.has(repository)) void this.loadTags(repository);
  }

  async loadTags(repository: string, force = false) {
    const connectionId = this.selectedConnectionId;
    if (!connectionId) return;
    if (!force && this.tagsByRepo.has(repository)) return;
    // This call may write only while it is the latest for its repository and
    // the catalog is still the one it was asked about. A call that lost either
    // way writes nothing and clears no flag: the winner owns the spinner (the
    // old shape left another registry's tags on this one, or cleared a
    // spinner a newer read was still showing).
    const catalog = this.catalogSeq;
    const call = ++this.tagCallSeq;
    const ended = sessionMark(this.rootStore);
    this.tagCalls.set(repository, call);
    const current = () =>
      catalog === this.catalogSeq && this.tagCalls.get(repository) === call && !ended();
    this.loadingTags.add(repository);
    this.tagsUnavailable.delete(repository);
    this.tagsErrors.delete(repository);
    try {
      const result = await findOrThrow(this.tagsService, {
        query: { registryConnectionId: connectionId, repository },
      });
      const rows = rowsOf<RegistryTag>(
        result as unknown as FindResponse<RegistryTag>
      );
      runInAction(() => {
        if (!current()) return;
        this.tagsByRepo.set(repository, rows);
        if (rows.length === 0) this.tagsUnavailable.add(repository);
      });
    } catch (err: unknown) {
      console.error(`Failed to load tags for ${repository}:`, err);
      // A sign-out: reset() emptied these maps, and writing the repository
      // name back into them would carry it into the next person's page.
      if (isSessionEnded(err)) return;
      runInAction(() => {
        if (!current()) return;
        this.tagsByRepo.set(repository, []);
        this.tagsErrors.set(
          repository,
          loadErrorMessage(err, "Failed to load tags")
        );
      });
    } finally {
      runInAction(() => {
        if (current()) this.loadingTags.delete(repository);
      });
    }
  }

  /** Refresh: reload the selected connection's repositories (and open tags). */
  refresh() {
    void this.loadRepositories();
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.catalogSeq++;
    this.tagCalls.clear();
    this.loadingRepos = false;
    this.error = null;
    this.connectionsError = null;
    this.selectedConnectionId = null;
    this.searchQuery = "";
    this.repositories = [];
    this.expanded = new Set<string>();
    this.tagsByRepo = new Map<string, RegistryTag[]>();
    this.loadingTags = new Set<string>();
    this.tagsUnavailable = new Set<string>();
    this.tagsErrors = new Map<string, string>();
    this.tagSorts = new Map<string, { key: RegistryTagSortKey; dir: SortDir }>();
  }
}

export default RegistryImagesStore;
